import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";
import { Phone, Mail, Calendar, User, MessageSquare, ArrowRight, Trash2, UserX } from "lucide-react";
import dayjs from "dayjs";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Shows details for an appointment. For walk-in bookings (no patient_id) it
 * displays the wellness-hub booking fields (name, phone, email, condition,
 * message, age). For linked appointments it links to the patient profile.
 *
 * Optional callback:
 *  - onDeleted?(id): fired after successful deletion so the caller can refresh.
 */
export default function AppointmentDetailModal({ appointment, open, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  if (!appointment) return null;
  const a = appointment;
  const isWalkIn = !a.patient_id;

  const handleDelete = async (reason) => {
    if (!a.id) return;
    const msg =
      reason === "no-show"
        ? "Delete this appointment as a no-show? This cannot be undone."
        : "Delete this appointment? This cannot be undone.";
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      await api.delete(`/appointments/${a.id}`);
      toast.success(reason === "no-show" ? "Marked no-show and removed" : "Appointment deleted");
      onDeleted?.(a.id);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="appointment-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <User className="size-5 text-primary" /> {a.patient_name || a.name || "Appointment"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            {isWalkIn && (
              <Badge variant="secondary" data-testid="online-badge">
                {a.source === "online" || a.preferred_time ? "Online booking" : "Walk-in booking"}
              </Badge>
            )}
            {a.consultation_type && (
              <Badge variant="outline">{a.consultation_type}</Badge>
            )}
            <Badge className="capitalize">{a.status || "scheduled"}</Badge>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Info
              icon={Calendar}
              label="When"
              value={`${dayjs(a.scheduled_at).format("MMM D, YYYY")} · ${
                a.display_time || dayjs(a.scheduled_at).format("h:mm A")
              }`}
            />
            {a.phone && <Info icon={Phone} label="Phone" value={a.phone} />}
            {a.email && <Info icon={Mail} label="Email" value={a.email} />}
            {a.age && <Info icon={User} label="Age" value={String(a.age)} />}
          </div>

          {(a.reason || a.condition) && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-secondary font-bold">
                Reason
              </p>
              <p className="text-foreground/90 mt-1">{a.reason || a.condition}</p>
            </div>
          )}

          {a.message && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-secondary font-bold flex items-center gap-1">
                <MessageSquare className="size-3" /> Message
              </p>
              <p className="text-foreground/90 mt-1 whitespace-pre-wrap">{a.message}</p>
            </div>
          )}

          {a.notes && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-secondary font-bold">
                Notes
              </p>
              <p className="text-foreground/90 mt-1">{a.notes}</p>
            </div>
          )}

          <div className="pt-3 border-t border-border flex flex-col gap-2">
            {!isWalkIn && (
              <Link to={`/patients/${a.patient_id}`}>
                <Button className="w-full" data-testid="modal-open-patient-btn">
                  Open patient profile <ArrowRight className="size-4 ml-2" />
                </Button>
              </Link>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 border-amber-300 text-amber-800 hover:bg-amber-50"
                onClick={() => handleDelete("no-show")}
                disabled={deleting}
                data-testid="modal-no-show-btn"
              >
                <UserX className="size-4 mr-2" /> No-show
              </Button>
              <Button
                variant="outline"
                className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                onClick={() => handleDelete("cancel")}
                disabled={deleting}
                data-testid="modal-delete-appt-btn"
              >
                <Trash2 className="size-4 mr-2" /> Delete
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Info = ({ icon: Icon, label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-widest text-secondary font-bold flex items-center gap-1">
      <Icon className="size-3" /> {label}
    </p>
    <p className="text-foreground/90 mt-0.5">{value}</p>
  </div>
);
